import React from 'react';
import type { RealestateAd, RealestateProjectAd } from '../../../types';
import { SearchAd } from '../SearchAd';
import { useSearchAdClasses } from '../SearchAdClasses';
import type { BaseSearchAdProps } from '../SearchAdTypes';
import { RealestateBullets } from './RealestateBullets';
import { RealestateHeading } from './RealestateHeading';
import { RealestateImage } from './RealestateImage';
import { BasicRealestateLabel } from './RealestateLabel';
import { RealestateLocation } from './RealestateLocation';
import { RealestateOrganisation } from './RealestateOrganisation';
import { RealestateSummary } from './RealestateSummary';

export const classes = {
    mobile: {
        ad: 'grid grid-cols-3 grid-rows-2 s-bg',
        imageWrapper: 'mt-16 ml-16',
        imageAspect: 'h-full aspect-3/2',
        image: 'rounded-8 border s-border',
        content: 'col-span-3 m-16',
        organisation: 'col-span-2 m-16 mb-0 pr-44',
        location: 'mt-4',
        summary: 'mt-16 flex justify-between',
        bullets: 'flex flex-col mt-4',
        ribbon: 'absolute top-0 left-0 pointer-events-none',
    },
    desktop: {
        imageWrapper:
            'sm:m-0 sm:col-start-3 sm:row-span-2 sm:relative sf-realestate-image',
        imageAspect: 'sf-realestate-image-aspect-none sm:absolute sm:inset-0',
        image: 'sm:rounded-0 sm:border-0',
        content: 'sm:col-span-2 sm:mt-8',
        organisation: 'sm:row-start-1 sm:p-0',
        summary: 'sm:mt-8 sm:block',
        bullets: 'sm:block',
    },
};

export const adStyle: React.CSSProperties = {
    gridTemplateRows: 'min-content 1fr',
};

type BasicRealestateSearchAdProps = BaseSearchAdProps & {
    ad: RealestateAd | RealestateProjectAd;
    imageSizes?: string;
};

export function BasicRealestateSearchAd(props: BasicRealestateSearchAdProps) {
    const {
        ad,
        className = '',
        heading,
        image,
        imageSizes,
        label,
        ...searchAdProps
    } = props;

    const c = useSearchAdClasses({
        mode: searchAdProps.mode,
        mobileClasses: classes.mobile,
        desktopClasses: classes.desktop,
    });

    return (
        <SearchAd
            adId={ad.id}
            className={`${c.ad} ${className}`}
            style={adStyle}
            {...searchAdProps}
        >
            <div className={c.imageWrapper}>
                <RealestateImage
                    ad={ad}
                    aspectClass={c.imageAspect}
                    image={image}
                    imageClass={c.image}
                    sizes={imageSizes}
                />
                <BasicRealestateLabel
                    ad={ad}
                    className={c.ribbon}
                    label={label}
                />
            </div>
            <RealestateOrganisation
                ad={ad}
                className={c.organisation}
                hideLogoOnMobile={true}
            />
            <div className={c.content}>
                <RealestateHeading ad={ad} heading={heading} />
                <RealestateLocation ad={ad} className={c.location} />
                <RealestateSummary ad={ad} className={c.summary} />
                <RealestateBullets ad={ad} className={c.bullets} />
            </div>
        </SearchAd>
    );
}
